import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import Seo from "./components/Seo";
import ClientIntake from "./pages/ClientIntake";
import RentalConsultation from "./pages/RentalConsultation";
import Gallery from "./pages/Gallery";
import Masonry from "react-masonry-css";
import { useTranslation } from "./i18n";
import cardImage from "./assets/card_image.jpeg";

// Import all photos from the assets/photos folder
const importAllPhotos = (r: any) => {
  return r.keys().map(r);
};

// Get all photo files from the photos directory
const photoContext = (require as any).context(
  "./assets/photos",
  false,
  /\.(jpeg|jpg|png)$/
);
const allPhotos = importAllPhotos(photoContext);

// Function to get randomized photos for the collage
const getRandomizedPhotos = (count: number): string[] => {
  // Shuffle the array and take the first 'count' items
  const shuffled = [...allPhotos].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const PUBLIC_PHONE_HREF = "tel:+256781822010";
const PUBLIC_PHONE_DISPLAY = "+256 781 822 010";
const PUBLIC_EMAIL = "delythug@gmail.com";
const CONTACT_MAP_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1639.441060080596!2d32.604569439878276!3d0.35131077711521036!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbb56cb4b3f59%3A0xa54470f39add5cb3!2sDelyth%20Bridal%20Store!5e0!3m2!1sen!2sus!4v1764698035434!5m2!1sen!2sus";

const Home: React.FC = () => {
  const { raw, t } = useTranslation();
  const navigate = useNavigate();

  // State to store randomized photos - this will be different each time the component mounts
  const [collageImages] = React.useState(() => getRandomizedPhotos(12));

  const countries = raw<Array<{ name: string; flag: string }>>("home.countries");
  const reviews = raw<
    Array<{ id: number; rating: number; text: string; author: string }>
  >("home.reviews");


  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80; // Approximate navbar height
      const elementPosition = element.offsetTop - navbarHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  // Handle hash navigation when coming from other pages
  React.useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        scrollToSection(hash);
      }, 100);
    }
  }, []);

  return (
    <div>
      <Seo
        pageTitle={t("seo.homeTitle")}
        description={t("seo.defaultDescription")}
      />
      <Navigation scrollToSection={scrollToSection} />
      {/* <div className="relative overflow-hidden bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-black font-semibold py-2 px-8 text-lg tracking-wider flex justify-center items-center text-center">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
        <span className="relative z-10">🎉 END OF YEAR SALE - 30% OFF ALL GOWNS 🎉</span>
      </div> */}

      {/* Home Section */}
      <section id="home" className="min-h-screen">
        <div className="relative min-h-screen overflow-hidden bg-stone-950">
          {/* Collage Background */}
          <div className="absolute inset-0 opacity-100">
            <Masonry
              breakpointCols={{
                default: 4,
                1100: 3,
                700: 2,
                500: 1,
              }}
              className="flex gap-2"
              columnClassName="flex flex-col gap-2"
            >
              {collageImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={t("common.bridalDressAlt", { number: i + 1 })}
                  className="w-full rounded-lg object-cover"
                />
              ))}
            </Masonry>
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,251,246,0.34)_0%,rgba(249,242,233,0.62)_38%,rgba(247,241,233,0.92)_100%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.58),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(214,189,159,0.18),transparent_30%)]"></div>

          <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-4 pb-14 pt-28 sm:px-6 lg:px-8">
            <div className="w-full rounded-[2rem] border border-white/60 bg-[rgba(255,251,246,0.56)] p-4 shadow-[0_28px_90px_rgba(117,95,74,0.12)] backdrop-blur-xl sm:p-6 lg:p-8">
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-center lg:gap-12">
                <div className="px-3 py-4 sm:px-5 lg:pr-8">
                  <div className="inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.36em] text-stone-500 sm:text-xs">
                    <span className="h-px w-10 bg-[#c9af8a]"></span>
                    <span>Bespoke Bridal Atelier</span>
                  </div>

                  <h1 className="mt-7 text-5xl font-light tracking-[0.2em] text-stone-800 sm:text-6xl lg:text-[4.5rem] xl:text-[5rem]">
                    {t("home.hero.headline")}
                  </h1>

                  <p className="mt-8 max-w-xl text-lg leading-8 text-stone-700 sm:text-[1.35rem] sm:leading-9">
                    {t("home.hero.tagline")}
                  </p>

                  <div className="mt-9 flex flex-col gap-4 text-sm text-stone-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
                    <span className="inline-flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#bea07a]"></span>
                      {t("home.hero.consultation")}
                    </span>
                    <span className="inline-flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#bea07a]"></span>
                      {t("home.hero.shipping")}
                    </span>
                    <span className="inline-flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#bea07a]"></span>
                      {countries.length}+ {t("home.world.countLabel")}
                    </span>
                  </div>

                  <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      onClick={() => navigate("/tailor-my-dress")}
                      className="inline-flex w-full items-center justify-center rounded-full bg-[#2f241d] px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300 hover:bg-[#43342a] sm:w-auto"
                    >
                      {t("home.hero.designButton")}
                    </button>

                    <a
                      href={PUBLIC_PHONE_HREF}
                      className="inline-flex w-full items-center justify-center rounded-full border border-[#d8c7b2] bg-[#fffaf4]/82 px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-800 backdrop-blur-sm transition duration-300 hover:border-[#bea07a] hover:bg-[#fffdf9] sm:w-auto"
                    >
                      Call Atelier
                    </a>
                  </div>
                </div>

                <div className="relative">
                  <div className="overflow-hidden rounded-[1.75rem] border border-white/70 bg-white/50 p-3 shadow-[0_20px_60px_rgba(117,95,74,0.12)]">
                    <div className="relative aspect-[4/5] overflow-hidden rounded-[1.35rem] bg-[#ece4db]">
                      <img
                        src={cardImage}
                        alt={t("common.bridalDressAlt", { number: 1 })}
                        className="h-full w-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(37,28,20,0.18))]"></div>
                    </div>
                  </div>

                  {/* <div className="absolute left-6 top-6 rounded-full border border-white/20 bg-[rgba(47,36,29,0.62)] px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-[#f8efe4] shadow-sm backdrop-blur-md">
                    Bridal Couture
                  </div> */}

                  <div className="absolute inset-x-5 bottom-5 rounded-[1.1rem] border border-white/45 bg-[rgba(255,248,242,0.56)] px-3.5 py-2.5 shadow-lg backdrop-blur-sm sm:inset-x-6 sm:bottom-6 sm:px-4 sm:py-3">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-stone-600">
                      {t("home.hero.featuredCollection.label")}
                    </p>
                    <div className="mt-2 flex flex-col gap-1.5">
                      <p className="text-[0.95rem] font-light leading-snug text-stone-900 sm:text-[1rem]">
                        {t("home.hero.featuredCollection.title")}
                      </p>
                      <p className="text-[0.78rem] leading-4 text-stone-700">
                        {t("home.hero.featuredCollection.description")}
                      </p>
                      <a
                        href={PUBLIC_PHONE_HREF}
                        className="inline-block text-[0.82rem] font-semibold tracking-[0.03em] text-[#7d5d3f] transition-colors duration-300 hover:text-[#62452c]"
                      >
                        {PUBLIC_PHONE_DISPLAY}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">
              {t("home.about.title")}
            </h1>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                  {t("home.about.storyTitle")}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {t("home.about.storyParagraph1")}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {t("home.about.storyParagraph2")}
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                  {t("home.about.promiseTitle")}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {t("home.about.promiseParagraph1")}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {t("home.about.promiseParagraph2")}
                </p>
              </div>
            </div>

            {/* Global Reach Section */}
            <div className="mt-16">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                {t("home.world.title")}
              </h2>
              <p className="text-lg text-gray-600 mb-8 text-center max-w-2xl mx-auto">
                {t("home.world.description")}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 max-w-4xl mx-auto">
                {countries.map((country, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300 group"
                  >
                    <span className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">
                      {country.flag}
                    </span>
                    <span className="text-sm text-gray-700 text-center font-medium">
                      {country.name}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <p className="text-gray-600 text-lg">
                  <span className="font-semibold text-yellow-600">
                    {countries.length}+
                  </span>{" "}
                  {t("home.world.countLabel")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection Section */}
      {/* <section id="collection" className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">
            Bespoke Collection
          </h1>
          <p className="text-lg text-gray-600 mb-8 text-center max-w-2xl mx-auto">
            Explore our curated selection of bespoke bridal gowns available for
            both purchase and rental, or let us create a completely custom
            design just for you.
          </p>
          {/* <div className="text-center mb-12">
            <button
              onClick={() => navigate("/tailor-my-dress")}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-4 px-8 text-lg transition duration-300 tracking-wider"
            >
              TAILOR MY DREAM DRESS
            </button>
          </div> */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Product 1 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Bridal Dress 1"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Elegant A-Line Gown
              </h3>
              <p className="text-gray-600">Purchase: $2,500 • Rental: $800</p>
            </div>

            {/* Product 2 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Bridal Dress 2"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Vintage Lace Dress
              </h3>
              <p className="text-gray-600">Purchase: $3,200 • Rental: $950</p>
            </div>

            {/* Product 3 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1571513722639-8a4a0a4b4b4b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Bridal Dress 3"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Modern Ball Gown
              </h3>
              <p className="text-gray-600">Purchase: $2,800 • Rental: $850</p>
            </div>

            {/* Product 4 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Bridal Dress 4"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Romantic Mermaid
              </h3>
              <p className="text-gray-600">Purchase: $3,500 • Rental: $1,100</p>
            </div>

            {/* Product 5 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Bridal Dress 5"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Classic Princess
              </h3>
              <p className="text-gray-600">Purchase: $2,200 • Rental: $650</p>
            </div>

            {/* Product 6 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1571513722639-8a4a0a4b4b4b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Bridal Dress 6"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Boho Chic</h3>
              <p className="text-gray-600">Purchase: $2,900 • Rental: $875</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Rentals Section */}
      {/* <section id="rentals" className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">
            Rental Collection
          </h1>
          <p className="text-lg text-gray-600 mb-8 text-center max-w-2xl mx-auto">
            Discover our exclusive rental gowns - perfect for your special day
            without the commitment of purchase. Each dress is professionally
            maintained and ready to make your dreams come true.
          </p>
          <div className="text-center mb-12">
            <button
              onClick={() => navigate("/rental-consultation")}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-4 px-8 text-lg transition duration-300 tracking-wider"
            >
              BOOK A RENTAL CONSULTATION
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Rental Product 1 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Rental Dress 1"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Classic A-Line Rental
              </h3>
              <p className="text-gray-600">Rental: $400/week • Size: 6-12</p>
              <div className="mt-2">
                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Available
                </span>
              </div>
            </div>

            {/* Rental Product 2 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Rental Dress 2"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Vintage Lace Rental
              </h3>
              <p className="text-gray-600">Rental: $550/week • Size: 4-10</p>
              <div className="mt-2">
                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Available
                </span>
              </div>
            </div>

            {/* Rental Product 3 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1571513722639-8a4a0a4b4b4b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Rental Dress 3"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Modern Ball Gown Rental
              </h3>
              <p className="text-gray-600">Rental: $450/week • Size: 8-14</p>
              <div className="mt-2">
                <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                  Booked
                </span>
              </div>
            </div>

            {/* Rental Product 4 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Rental Dress 4"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Romantic Mermaid Rental
              </h3>
              <p className="text-gray-600">Rental: $600/week • Size: 6-10</p>
              <div className="mt-2">
                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Available
                </span>
              </div>
            </div>

            {/* Rental Product 5 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Rental Dress 5"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Princess Style Rental
              </h3>
              <p className="text-gray-600">Rental: $350/week • Size: 4-12</p>
              <div className="mt-2">
                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Available
                </span>
              </div>
            </div>

            {/* Rental Product 6 */}
            {/* <div className="group cursor-pointer">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-4">
                <img
                  src="https://images.unsplash.com/photo-1571513722639-8a4a0a4b4b4b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Rental Dress 6"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Boho Chic Rental
              </h3>
              <p className="text-gray-600">Rental: $500/week • Size: 6-14</p>
              <div className="mt-2">
                <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                  Reserved
                </span>
              </div>
            </div>
          </div>

          {/* Rental Information */}
          {/* <div className="mt-16 bg-gray-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Rental Information
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  What's Included
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    Professional cleaning and pressing
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    Minor alterations included
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    Garment bag for transport
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    Insurance coverage
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  Rental Terms
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    7-day rental period
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    $200 security deposit required
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    Free pickup and delivery
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                    Damage protection included
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Reviews Section */}
      <section id="reviews" className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-12 text-center">
            {t("home.reviewsTitle")}
          </h1>
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review) => (
              <div key={review.id} className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(review.rating)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 mb-4">"{review.text}"</p>
                <p className="font-semibold text-gray-800">- {review.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-800 mb-12 text-center">
              {t("home.contact.title")}
            </h1>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-8">
                  {t("home.contact.getInTouch")}
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">
                        {t("home.contact.address")}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {[
                          t("home.contact.addressLine1"),
                          t("home.contact.addressLine2"),
                          t("home.contact.addressLine3"),
                          t("home.contact.addressLine4"),
                        ]
                          .filter(Boolean)
                          .map((line, index) => (
                            <React.Fragment key={line}>
                              {index > 0 ? <br /> : null}
                              {line}
                            </React.Fragment>
                          ))}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">
                        {t("home.contact.phone")}
                      </h3>
                      <a
                        href={PUBLIC_PHONE_HREF}
                        className="text-gray-600 hover:text-yellow-600 transition-colors duration-300 text-lg"
                      >
                        {PUBLIC_PHONE_DISPLAY}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">
                        {t("home.contact.email")}
                      </h3>
                      <a
                        href={`mailto:${PUBLIC_EMAIL}`}
                        className="text-gray-600 hover:text-yellow-600 transition-colors duration-300 break-all"
                      >
                        {PUBLIC_EMAIL}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">
                        {t("home.contact.hours")}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {t("home.contact.hoursWeekdays")}
                        <br />
                        {t("home.contact.hoursSaturday")}
                        <br />
                        {t("home.contact.hoursSunday")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-8">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                    {t("home.contact.findUs")}
                  </h2>
                  <div className="rounded-lg overflow-hidden shadow-md">
                    <iframe
                      src={CONTACT_MAP_EMBED_URL}
                      width="100%"
                      height="450"
                      className="border-0 w-full"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={t("home.contact.mapTitle")}
                    ></iframe>
                  </div>
                </div>
              {/* <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-6">
                  Send us a Message
                </h2>
                <form className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-6 rounded-lg transition duration-300"
                  >
                    Send Message
                  </button>
                </form>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

function App() {
  const { locale, t, toggleLocale } = useTranslation();
  const nextLocale = locale === "en" ? "fr" : "en";

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/tailor-my-dress" element={<ClientIntake />} />
          <Route path="/rental-consultation" element={<RentalConsultation />} />
          <Route path="/gallery" element={<Gallery />} />
        </Routes>
        
        <button
          type="button"
          onClick={toggleLocale}
          className="fixed bottom-24 right-6 flex items-center gap-2 bg-white text-gray-800 border border-gray-200 px-4 py-3 rounded-full shadow-lg hover:scale-110 transition-transform duration-300 z-50 font-semibold tracking-wide"
          aria-label={`${t("common.switchLanguage")}: ${t(
            `common.languageNames.${nextLocale}`
          )}`}
          title={`${t("common.switchLanguage")}: ${t(
            `common.languageNames.${nextLocale}`
          )}`}
        >
          <svg
            className="w-5 h-5 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-9c2.5 2.4 4 5.6 4 9s-1.5 6.6-4 9m0-18c-2.5 2.4-4 5.6-4 9s1.5 6.6 4 9m-8-9h16M5.5 7.5h13M5.5 16.5h13"
            />
          </svg>
          <span>{nextLocale.toUpperCase()}</span>
        </button>

        {/* Floating Instagram Icon */}
        <a
          href="https://www.instagram.com/delyth.brides/"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform duration-300 z-50"
          aria-label={t("common.followInstagram")}
        >
          <svg
            className="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        </a>
      </div>
    </Router>
  );
}

export default App;
